import { useEffect, useState } from 'react';

import { useDebounceEffect } from 'ahooks';

import { formatWholeNumber, parseWholeNumber } from './grm';
import type { GrmField, GrmValues } from './grm';
import type { PropertyState } from './PropertyCard';

type PropertyKey = 'a' | 'b';

const PARAM_PREFIX = 'grm_';
const COMPARE_PARAM = `${PARAM_PREFIX}compare`;

const FIELD_PARAMS: Record<GrmField, string> = {
  price: 'price',
  rentPerUnit: 'rent',
  units: 'units',
  otherIncome: 'other',
};

const FIELDS = Object.keys(FIELD_PARAMS) as Array<GrmField>;

const getParamName = (property: PropertyKey, field: GrmField) => `${PARAM_PREFIX}${property}_${FIELD_PARAMS[field]}`;

const readValues = (params: URLSearchParams, property: PropertyKey, defaults: GrmValues): GrmValues => {
  const values = { ...defaults };
  FIELDS.forEach((field) => {
    const param = params.get(getParamName(property, field));
    if (param !== null && /^\d+$/.test(param)) {
      values[field] = formatWholeNumber(param);
    }
  });
  return values;
};

const writeValues = (url: URL, property: PropertyKey, values: GrmValues | null, defaults: GrmValues) => {
  FIELDS.forEach((field) => {
    const name = getParamName(property, field);
    const value = values ? parseWholeNumber(values[field]) : null;
    if (value === null || value === parseWholeNumber(defaults[field])) {
      url.searchParams.delete(name);
    } else {
      url.searchParams.set(name, String(value));
    }
  });
};

const useGrmUrlState = (defaultA: PropertyState, defaultB: PropertyState) => {
  const [propertyA, setPropertyA] = useState(defaultA);
  const [propertyB, setPropertyB] = useState(defaultB);
  const [isComparing, setIsComparing] = useState(false);
  const [isUrlRead, setIsUrlRead] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPropertyA((property) => ({ ...property, values: readValues(params, 'a', defaultA.values) }));
    if (params.get(COMPARE_PARAM) === '1') {
      setPropertyB((property) => ({ ...property, values: readValues(params, 'b', defaultB.values) }));
      setIsComparing(true);
    }
    setIsUrlRead(true);
  }, [defaultA, defaultB]);

  useDebounceEffect(
    () => {
      if (!isUrlRead) {
        return;
      }
      const url = new URL(window.location.href);
      writeValues(url, 'a', propertyA.values, defaultA.values);
      writeValues(url, 'b', isComparing ? propertyB.values : null, defaultB.values);
      if (isComparing) {
        url.searchParams.set(COMPARE_PARAM, '1');
      } else {
        url.searchParams.delete(COMPARE_PARAM);
      }
      if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', url);
      }
    },
    [propertyA, propertyB, isComparing, isUrlRead, defaultA, defaultB],
    { wait: 300 },
  );

  return { propertyA, setPropertyA, propertyB, setPropertyB, isComparing, setIsComparing };
};

export default useGrmUrlState;
